import type { RouteObject } from 'react-router';

import { Outlet } from 'react-router';
import { lazy, Suspense } from 'react';

import { CONFIG } from 'src/global-config';
import { DashboardLayout } from 'src/layouts/dashboard';
import { PermissionWrapper } from 'src/pages/auth/hooks/permissionWrapper';

import { LoadingScreen } from 'src/components/loading-screen';

import { AuthGuard } from 'src/auth/guard';

import { usePathname } from '../../../routes/hooks';

// Lazy-loaded pages
const OrganizationPage = lazy(() => import('src/pages/setting/organization'));
const UsersListPage = lazy(() => import('src/pages/setting/users-list'));
const TeamsListPage = lazy(() => import('src/pages/setting/teams-list'));
const CustomLabelListPage = lazy(() => import('src/pages/setting/custom-label-list'));
const TemplateListPage = lazy(() => import('src/pages/setting/template-list'));
const WhatsappTemplate = lazy(() => import('src/pages/setting/whatsapp-template'));
const DidManagePage = lazy(() => import('src/pages/setting/did-manage'));
const UserDetailsPage = lazy(() => import('src/pages/setting/user-details'));
const TeamDetailsPage = lazy(() => import('src/pages/setting/teamDetailPage'));
const BillingPage = lazy(() => import('src/pages/setting/billingPage'));
const WebhookPage = lazy(() => import('src/pages/setting/webhook-list'));

// Suspense wrapper with dynamic key for pathname
function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

// Layout wrapped in auth guard if needed
const getLayout = () =>
  CONFIG.auth.skip ? (
    <DashboardLayout>
      <SuspenseOutlet />
    </DashboardLayout>
  ) : (
    <AuthGuard>
      <DashboardLayout>
        <SuspenseOutlet />
      </DashboardLayout>
    </AuthGuard>
  );

// Helper to wrap a component with PermissionWrapper
const withPermission = (
  module: string,
  Component: React.LazyExoticComponent<() => JSX.Element>
) => (
  <PermissionWrapper module={module}>
    <Component />
  </PermissionWrapper>
);

// Route config
export const settingRoutes: RouteObject[] = [
  {
    path: 'setting',
    element: getLayout(),
    children: [
      {
        index: true,
        element: withPermission('user', UsersListPage),
      },
      {
        path: 'organization',
        element: withPermission('organization', OrganizationPage),
      },
      {
        path: 'user',
        element: null,
        children: [
          {
            index: true,
            element: withPermission('user', UsersListPage),
          },
          {
            path: ':id/details',
            element: withPermission('user', UserDetailsPage),
          },
        ],
      },
      {
        path: 'teams',
        element: null,
        children: [
          {
            index: true,
            element: withPermission('user', TeamsListPage),
          },
          {
            path: ':id/details',
            element: withPermission('user', TeamDetailsPage),
          },
        ],
      },

      {
        path: 'custom-labels',
        element: withPermission('label', CustomLabelListPage),
      },
      {
        path: 'template',
        element: withPermission('template', TemplateListPage),
      },
      {
        path: 'whatsapp-template',
        element: withPermission('template', WhatsappTemplate),
      },
      {
        path: 'did-manage',
        element: withPermission('call', DidManagePage),
      },
      {
        path: 'billing',
        element: withPermission('bill', BillingPage),
      },
      {
        path: 'webhook',
        element: withPermission('webhook', WebhookPage),
      },
    ],
  },
];
